import { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { Direction } from '../components/RTLLayout';
import i18n from '../i18n/config';

const env = process.env;

const getDirectionForLang = (lng?: string): Direction => (lng && lng.startsWith('ar') ? 'rtl' : 'ltr');

interface Config {
  environment: string;
  apiBaseUrl: string;
  direction: Direction;
  updateDirection: (newDirection: Direction) => void;
}

const ConfigContext = createContext<Config | null>(null);

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

interface ConfigProviderProps {
  children: ReactNode;
}

export const ConfigProvider = ({ children }: ConfigProviderProps) => {
  const [direction, setDirection] = useState<Direction>(getDirectionForLang(i18n.language));

  const updateDirection = (newDirection: Direction) => {
    setDirection(newDirection);
  };

  // Keep direction in sync with the active i18n language (fixes first-load mismatch)
  useEffect(() => {
    const syncDirection = (lng: string) => setDirection(getDirectionForLang(lng));
    syncDirection(i18n.language);
    i18n.on('languageChanged', syncDirection);
    i18n.on('initialized', () => syncDirection(i18n.language));
    return () => {
      i18n.off('languageChanged', syncDirection);
    };
  }, []);

  useEffect(() => {
    document.dir = direction;
    if (i18n.language) {
      document.documentElement.lang = i18n.language;
    }
  }, [direction]);

  const config: Config = {
    environment: env.ENVIRONMENT || 'development',
    apiBaseUrl: env.API_BASE_URL || '',
    direction,
    updateDirection
  };

  return (
    <ConfigContext.Provider value={config}>
      {children}
    </ConfigContext.Provider>
  );
}; 